import React from 'react';
import Slider from 'react-slick';
import { PrismicRichText } from '@prismicio/react';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import { BREAKPOINT, MEDIA } from '@constants';
import { PageSlice, QuoteTextData, QuoteTextItem } from '@contracts';
import useDesktop from '../useDesktop';
import QuoteBox from './QuoteBox';
import { TEXT_STYLES, TextVariant, Typography } from './Typography';

const settings = {
  slidesToShow: 1.1,
  initialSlide: 0,
  arrows: false,
  dots: false,
  adaptiveHeight: true,
  draggable: true,
  infinite: true
};

interface Props {
  slice: PageSlice;
}

const QuoteText: React.FC<Props> = ({ slice }) => {
  const isMobile = !useDesktop(BREAKPOINT.mobileL);
  const sliceDetails = slice.primary as QuoteTextData;
  const sliceItems = slice.items as QuoteTextItem[];

  return (
    <Container>
      <TextWrap>
        {sliceDetails.overline1 && <Typography $variant={TextVariant.UpperText4}>{sliceDetails.overline1}</Typography>}
        <Heading>{sliceDetails.heading?.richText && <PrismicRichText field={sliceDetails.heading.richText} />}</Heading>
        <Subheading>{sliceDetails.subheading?.richText && <PrismicRichText field={sliceDetails.subheading.richText} />}</Subheading>
      </TextWrap>
      {isMobile ? (
        <SliderWrap>
          <Slider {...settings}>
            {sliceItems.map((item, index) => (
              <SlideInner key={index}>
                <QuoteBox data={item} key={index} backgroundColor={sliceDetails.background_color ?? Colors.mint} />
              </SlideInner>
            ))}
          </Slider>
        </SliderWrap>
      ) : (
        <QuotesContainer>
          {sliceItems.map((item, index) => (
            <QuoteBox data={item} key={index} backgroundColor={sliceDetails.background_color ?? Colors.mint} />
          ))}
        </QuotesContainer>
      )}
    </Container>
  );
};

export default QuoteText;

const Container = styled.div`
  color: ${Colors.navyBlue};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  margin: auto;
  gap: 40px;
  justify-content: center;
  padding: 64px;
  transition: all 0.3s ease-in-out;
  will-change: width, padding;
  max-width: 1400px;

  ${MEDIA.mobileL} {
    width: 100vw;
    justify-content: center;
    padding: 56px 0;
    gap: 40px;
  }
`;

const SlideInner = styled.div`
  display: flex;
  justify-content: center;
  text-align: center;
  padding: 0 20px;
  box-sizing: border-box;
  transition: all 0.3s ease-in-out;
  will-change: padding, width;

  ${MEDIA.mobileL} {
    align-items: start;
    padding: 0px;
  }
`;

const SliderWrap = styled.div`
  width: 100%;
  overflow: visible;
  padding-left: 0px;
  padding-right: 0px;

  ${MEDIA.mobileL} {
    & .slick-list {
      padding-left: 56px !important;
    }
  }
`;

const QuotesContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 32px;
  width: 100%;
  transition: all 0.3s ease-in-out;
  will-change: gap, width;

  ${MEDIA.mobileL} {
    gap: 16px;
  }
`;

const TextWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  max-width: 60%;

  ${MEDIA.mobileL} {
    max-width: 100%;
    padding: 0 16px;
  }
`;

const Heading = styled.div`
  text-align: center;
  & > * {
    ${TEXT_STYLES[TextVariant.Heading3]};
    color: inherit;

    ${MEDIA.tablet} {
      ${TEXT_STYLES[TextVariant.Heading5]};
    }
  }
`;

const Subheading = styled.div`
  text-align: center;
  & > * {
    ${TEXT_STYLES[TextVariant.BodyRegularLG]};
    color: inherit;

    ${MEDIA.mobileM} {
      ${TEXT_STYLES[TextVariant.BodyRegularMD]};
    }
  }
`;
